import { useEffect } from 'react';
import { useData } from '@/context/DataContext';

export default function ContactPage() {
  const { settings, fetchSettings, loading } = useData();

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="contact-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-normal text-[#2c3e50] mb-4">Kontakt</h1>
        <hr className="mb-8 border-gray-300" />

        {/* Contact Info + Image Section */}
        <div className="grid md:grid-cols-5 gap-8 mb-8">
          {/* Left Column - Contact Info (37%) */}
          <div className="md:col-span-2">
            <h3 className="text-[#7f8c8d] text-lg mb-1">M.Sc.-Psych.</h3>
            <h2 className="text-2xl font-semibold text-[#2c3e50] mb-1">Katharina Langer</h2>
            <h3 className="text-[#7f8c8d] text-lg mb-6">Psychologische Psychotherapeutin</h3>

            <div className="text-[#2c3e50] space-y-1 mb-4">
              <p><strong>Butenwall 52A, 46325 Borken</strong></p>
              <p><strong>Gremmendorfer Weg 83, 48167 Münster</strong></p>
              <p><strong>Telefon:</strong> {settings?.phone || '+49 15751336474'}</p>
            </div>

            <p>
              <a 
                href={`mailto:${settings?.email || 'Psychotherapie-Praxis-langer@gmx.de'}`}
                className="text-[#3498db] hover:underline"
              >
                {settings?.email || 'Psychotherapie-Praxis-langer@gmx.de'}
              </a>
            </p>
          </div>

          {/* Right Column - Image (63%) */}
          <div className="md:col-span-3">
            <img 
              src="https://psychotherapie-praxis-langer.de/Bilder/Katharina-Langer.jpg"
              alt="Katharina Langer"
              className="w-full"
              loading="lazy"
            />
          </div>
        </div>

        <hr className="my-8 border-gray-300" />

        {/* Appointment Section with Online Booking */}
        <div className="grid md:grid-cols-5 gap-8 mb-8">
          <div className="md:col-span-2">
            <h3 className="text-lg text-[#2c3e50] mb-4">
              Termine vereinbaren Sie bitte telefonisch oder online montags bis freitags oder per E-Mail. Hier gehts zur
            </h3>
            <p className="font-semibold text-[#2c3e50] mb-4">Online Buchung:</p>
            {/* Jameda Booking Widget - Embedded Calendar */}
            <div className="w-full" style={{ minHeight: '400px' }}>
              <iframe
                src="https://www.jameda.de/ajax/marketing/doctor/widget/big_with_calendar/katharina-langer/null?header=null&content=null&fullwidth=true&hide_branding=true&widget_position=bottom&opinion=false&saasonly=false&expand_calendar=true"
                title="Jameda Online Buchung"
                style={{ 
                  border: 'none', 
                  overflow: 'hidden', 
                  width: '100%', 
                  height: '450px',
                  borderRadius: '8px'
                }}
                scrolling="no"
                allowTransparency="true"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="text-[#2c3e50] leading-relaxed text-lg">
              Sollten Sie mich nicht persönlich erreichen, hinterlassen Sie mir gerne 
              eine Nachricht auf dem Anrufbeantworter, ich rufe Sie so bald wie möglich zurück. 
              Natürlich können Sie mir auch eine E-Mail schreiben. Ich werde mich zeitnah melden.
            </p>
          </div>
        </div>

        <hr className="my-8 border-gray-300" />

        {/* Borken Location */}
        <div className="grid md:grid-cols-5 gap-8 mb-4">
          <div className="md:col-span-2">
            <h3 className="text-lg text-[#2c3e50] mb-2">
              Die Praxis in Borken finden Sie am Butenwall 52A
            </h3>
            <p className="text-[#2c3e50]"><strong>Butenwall 52A, 46325 Borken</strong></p>
          </div>
          <div className="md:col-span-3"></div>
        </div>
        <div className="w-full h-72 bg-gray-200 overflow-hidden mb-16">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2471.829!2d6.8545!3d51.8424!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8c5c14e7f3f13%3A0x4d3b7e8b9c9a8e7d!2sButenwall%2052A%2C%2046325%20Borken!5e0!3m2!1sde!2sde!4v1687288354522!5m2!1sde!2sde"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Praxis Borken"
          />
        </div>

        {/* Münster Location */}
        <div className="grid md:grid-cols-5 gap-8 mb-4">
          <div className="md:col-span-2">
            <h3 className="text-lg text-[#2c3e50] mb-2">
              Die Praxis in Münster finden Sie im Gremmendorfer Weg 83
            </h3>
            <p className="text-[#2c3e50]"><strong>Gremmendorfer Weg 83, 48167 Münster</strong></p>
          </div>
          <div className="md:col-span-3"></div>
        </div>
        <div className="w-full h-72 bg-gray-200 overflow-hidden">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2460.15226989161!2d7.67468047717136!3d51.93117607191269!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b9a4afe541209f%3A0x46a9b99119a51694!2sGremmendorfer%20Weg%2083%2C%2048167%20M%C3%BCnster!5e0!3m2!1sde!2sde!4v1687288354522!5m2!1sde!2sde"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Praxis Münster"
          />
        </div>
      </div>
    </div>
  );
}
